import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import {
    faFacebook,
    faInstagram,
    faLinkedin,
    faXTwitter,
    faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { IconDefinition, faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Account } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface SocialSettingsFormValues {
    website: string;
    linkedin: string;
    facebook: string;
    twitter: string;
    instagram: string;
    youtube: string;
}

const SocialSettings = () => {
    const toast = useToast();
    const { accountId, website, linkedin, facebook, twitter, instagram, youtube } =
        useCurrentAccount();

    const defaultValues: SocialSettingsFormValues = {
        website: website || '',
        linkedin: linkedin || '',
        facebook: facebook || '',
        twitter: twitter || '',
        instagram: instagram || '',
        youtube: youtube || '',
    };

    const methods = Form.useGivebutterForm<SocialSettingsFormValues>({ defaultValues });
    const { control, handleSubmit, setError, resetFormButton, path } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation<
        Account,
        GivebutterAxiosError,
        SocialSettingsFormValues
    >({
        mutationFn: values => updateAccount(accountId, values),
        onSuccess: () => {
            toast.success('Updated Social Accounts!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            toast.error(data?.message ?? 'Failed to update Social Accounts.');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof SocialSettingsFormValues, {
                        message: errors[field][0],
                    });
                }
            }
            resetFormButton();
        },
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>Social Accounts</SettingsCard.Heading>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(values => submit(values))}
                        className="flex flex-col gap-4"
                    >
                        <Form.Container>
                            <Form.Label className="sr-only text-base" name={path('website')}>
                                Website URL
                            </Form.Label>
                            <Form.TextInputRoot control={control} name="website">
                                <FontAwesomeIcon icon={faGlobe} />
                                <Form.TextInputForm placeholder="Enter Website" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('website')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label className="sr-only text-base" name={path('linkedin')}>
                                LinkedIn URL
                            </Form.Label>

                            <Form.TextInputRoot control={control} name="linkedin">
                                <FontAwesomeIcon icon={faLinkedin as IconDefinition} />
                                <Form.TextInputForm placeholder="Enter LinkedIn URL" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('linkedin')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label className="sr-only text-base" name={path('facebook')}>
                                Facebook URL
                            </Form.Label>
                            <Form.TextInputRoot control={control} name="facebook">
                                <FontAwesomeIcon icon={faFacebook as IconDefinition} />
                                <Form.TextInputForm placeholder="Enter Facebook URL" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('facebook')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label
                                title="Twitter"
                                className="sr-only text-base"
                                name={path('twitter')}
                            >
                                𝕏 URL
                            </Form.Label>
                            <Form.TextInputRoot control={control} name="twitter">
                                <FontAwesomeIcon icon={faXTwitter as IconDefinition} />
                                <Form.TextInputForm placeholder="Enter 𝕏 URL" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('twitter')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label className="sr-only text-base" name={path('instagram')}>
                                Instagram URL
                            </Form.Label>
                            <Form.TextInputRoot control={control} name="instagram">
                                <FontAwesomeIcon icon={faInstagram as IconDefinition} />
                                <Form.TextInputForm placeholder="Enter Instagram URL" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('instagram')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label className="sr-only text-base" name={path('youtube')}>
                                YouTube URL
                            </Form.Label>
                            <Form.TextInputRoot control={control} name="youtube">
                                <FontAwesomeIcon icon={faYoutube as IconDefinition} />
                                <Form.TextInputForm placeholder="Enter YouTube URL" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('youtube')} />
                        </Form.Container>

                        <Form.Container className="flex-row">
                            <Form.SubmitButton isSubmitting={isLoading}>Save</Form.SubmitButton>
                        </Form.Container>
                    </form>
                </Form.Provider>
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { SocialSettings };
